import { IResult } from '../models/HttpResult';
import {
  ContractIAImportProcess,
  NewContractIAImportProcess,
  ImportFeedbackRatingScaleItem,
  RateContractIAImportProcess,
  RateImportConflictError
} from '../models/Entities/Contracts/ContractIAImportProcess';
import { apiRequest, buildURL, buildHeaders, handleApiError } from './commons/CommonService.Functions';

const BaseURL = (process.env.REACT_APP_BILLER_API_GATEWAY || '').replace(/\/$/, '') + '/contracts';

// Create IA import process for a contract (POST /contracts/import)
export const CreateImportContractProcess = async (
  command: NewContractIAImportProcess,
  token: string
): Promise<ContractIAImportProcess> => {
  const url = buildURL(BaseURL, 'import');
  return apiRequest<ContractIAImportProcess>(url, token, 'CreateImportContractProcess', {
    method: 'POST',
    body: JSON.stringify(command)
  });
};

// Get last IA import process for a contract (GET /contracts/{ContractId}/import/last)
// Devuelve null si no existe proceso previo (404)
export const GetLastImportContractProcess = async (
  contractId: number,
  token: string
): Promise<ContractIAImportProcess | null> => {
  const url = buildURL(BaseURL, contractId, 'import', 'last');
  try {
    const response = await fetch(url, { headers: buildHeaders(token) });
    if (response.status === 404) return null;
    if (!response.ok) throw response;
    const data: IResult<ContractIAImportProcess> = await response.json();
    return data.data;
  } catch (error: any) {
    const handled = await handleApiError(error, 'GetLastImportContractProcess');
    throw handled;
  }
};


// Get the IA import history for a contract (GET /contracts/{ContractId}/import/history)
// The endpoint is paged; the widget only needs the most recent page.
export const GetImportContractProcessHistory = async (
  contractId: number,
  token: string,
  pageSize: number = 20
): Promise<ContractIAImportProcess[]> => {
  const url = buildURL(BaseURL, contractId, 'import', 'history') + '?PageNumber=1&PageSize=' + pageSize;
  try {
    const response = await fetch(url, { headers: buildHeaders(token) });
    if (response.status === 404) return [];
    if (!response.ok) throw response;
    const data: IResult<ContractIAImportProcess[]> = await response.json();
    return data.data || [];
  } catch (error: any) {
    const handled = await handleApiError(error, 'GetImportContractProcessHistory');
    throw handled;
  }
};

// Get the rating scale used to score an IA import (GET /contracts/import/feedback/scale)
export const GetImportFeedbackRatingScale = async (
  token: string
): Promise<ImportFeedbackRatingScaleItem[]> => {
  const url = buildURL(BaseURL, 'import', 'feedback', 'scale');
  try {
    const response = await fetch(url, { headers: buildHeaders(token) });
    if (!response.ok) throw response;
    const data: IResult<ImportFeedbackRatingScaleItem[]> = await response.json();
    return data.data || [];
  } catch (error: any) {
    const handled = await handleApiError(error, 'GetImportFeedbackRatingScale');
    throw handled;
  }
};

// Register the rating and optional comment for a finished process
// (POST /contracts/import/{ProcessId}/feedback).
//
// The rating is immutable: a process that already has one answers 409 carrying
// the standing rating. That body is worth keeping — it lets the caller explain
// the rejection and show the existing score without a second request — so the
// 409 is not funnelled through handleApiError like the rest.
export const RateImportContractProcess = async (
  command: RateContractIAImportProcess,
  token: string
): Promise<ContractIAImportProcess> => {
  const url = buildURL(BaseURL, 'import', command.processId, 'feedback');
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(token),
      body: JSON.stringify(command)
    });
  } catch (error: any) {
    throw await handleApiError(error, 'RateImportContractProcess');
  }

  if (response.status === 409) {
    const body: IResult<ContractIAImportProcess> = await response.json().catch(() => ({} as any));
    const conflict: RateImportConflictError = {
      ErrorCode: 409,
      ErrorMessage: body?.message || 'La importación ya fue calificada y la calificación no puede modificarse.',
      Errors: (body as any)?.errors || [],
      alreadyRatedProcess: body?.data ?? undefined
    };
    throw conflict;
  }

  if (!response.ok) {
    throw await handleApiError(response, 'RateImportContractProcess');
  }

  const data: IResult<ContractIAImportProcess> = await response.json();
  return data.data;
};
